import { IPromise } from 'angular';
import * as React from 'react';
import { call, put, select } from 'redux-saga/effects';

import { ShareDialog, ShareDialogProps } from '@tableau/share-dialog';
import { DialogPositioningBehavior, DialogType } from '@tableau/widgets';

import { Messages } from '../../../../codegen/Localize';
import { ShareDialogUserActionOptions, ShareDialogUserActionsEvent, TelemetryEvents } from '../../../../common/telemetryEvents/TelemetryEvents';
import { DatasourceContentType } from '../../../../ts/models/ContentTypes';
import { IDataSourceInfo } from '../../../../ts/models/IDataSourceInfo';
import { IViewDetailInfo } from '../../../../ts/models/IViewInfo';
import { IExplorableItemTypeInfo } from '../../../../ts/models/resourceInfoParts';
import { Views } from '../../../../ts/resources/Views';
import { ModalInstance, ReactModalOptions, ReactModalService } from '../../../../ts/services/ReactModalService';
import { Telemetry } from '../../../../ts/services/TelemetryService';
import { IToastOptions, ToasterService, ToastType } from '../../../../ts/services/ToasterService';
import { requestFailed } from '../../../actions/RequestFailedAction';
import { ShareContentAction } from '../../../actions/spaces/ShareContentAction';
import { SessionSiteSelectors } from '../../../selectors/SessionInfoSelectors';
import { siteUrlsSelector } from '../../../selectors/SiteUrlsSelectors';
import { ServiceLocator } from '../../../ServiceLocator';
import { datasourceShareUrl, flowShareUrl, metricShareUrl, projectShareUrl, ShareOrigin, viewShareUrl, workbookShareUrl } from '../../../spaces/ShareUrls';

const shareDialogTestId = 'share-dialog';

/*
* getBaseDialogOptions() - constant properties for share dialog which are reused in tests
* Why it's method: https://tableau.slack.com/archives/C9H9ERB5G/p1553880792040700
*/
const getBaseDialogOptions = () => {
  return {
    testId: shareDialogTestId,
    type: DialogType.NormalWithTitleBar,
    dialogWidth: 500,
    showCloseButton: true,
    positioningBehavior: DialogPositioningBehavior.Draggable,
    shouldReturnFocusOnClose: true,
  };
};

const popToast = (toast: IToastOptions): void => {
  const service = ServiceLocator.get<ToasterService>('ToasterService');
  service.pop(toast);
};

/**
 * Callback method to show toast in VizportalClient.
 * @param message toast message text sent from share-dialog
 */
const toastHandler = (message: string, isInfo: boolean): void => {
  popToast({
    text: message,
    type: isInfo ? ToastType.Success : ToastType.Error,
  });
};

const logEvent = (event: ShareDialogUserActionsEvent): void => {
  const telemetry = ServiceLocator.get<Telemetry>('TelemetryService');
  const logEvent = telemetry.sendEvent<ShareDialogUserActionsEvent>(
    {
      type: event.type,
      UserActionType: event.UserActionType,
      Args: event.Args
    }
  );
};

/**
 * Wrapper function for telemetry callback. Share dialog calls this function, to log user
 * actions to PUT database, when user clicks on copy link, share button etc
 * @param userActionType One of the user action in ShareDialogUserActionOptions
 * @param args (optional) params
 */
const telemetryLogger = (userActionType: ShareDialogUserActionOptions, args?: string): void => {
  logEvent({ type: TelemetryEvents.ShareDialogUserActions, UserActionType: userActionType, Args: args });
};

let modalInstance: ModalInstance;
/**
 * Callback method to close dialog in VizportalClient.
 */
const closeDialogHandler = (): void => {
  modalInstance.close();

  telemetryLogger(ShareDialogUserActionOptions.CloseShareDialog);
};

function* handleShareContentAction(action: ShareContentAction) {
  try {
    const content = action.content;

    const siteUrls = yield select(siteUrlsSelector);
    const isNamedSharingEnabled = yield select(SessionSiteSelectors.isNamedSharingEnabled);
    let shareLink: string;
    let title: string;

    switch (content.contentType) {
      case 'view':
      case 'recommendedView':
        const viewDetailInfo: IViewDetailInfo = yield call(getViewDetailInfo, content);
        shareLink = yield call(viewShareUrl, siteUrls, viewDetailInfo.path, ShareOrigin.CardLink);
        title = Messages.shareDialogTitleForView();
        break;
      case 'project':
        shareLink = yield call(projectShareUrl, siteUrls, content.id, ShareOrigin.CardLink);
        title = Messages.shareDialogTitleForProject();
        break;
      case 'workbook':
        shareLink = yield call(workbookShareUrl, siteUrls, content.id, ShareOrigin.CardLink);
        title = Messages.shareDialogTitleForWorkbook();
        break;
      case 'datasource':
        shareLink = yield call(datasourceShareUrl, siteUrls, content.id, ShareOrigin.CardLink);
        title = Messages.shareDialogTitleForDatasource();
        break;
      case 'flow':
        shareLink = yield call(flowShareUrl, siteUrls, content.id, ShareOrigin.CardLink);
        title = Messages.shareDialogTitleForFlow();
        break;
      case 'metric':
        shareLink = yield call(metricShareUrl, siteUrls, content.id, ShareOrigin.CardLink);
        title = Messages.shareDialogTitleForMetric();
        break;
      default:
        return;
    }

    const shareDialogProps: ShareDialogProps = {
      closeDialogHandler,

      // This addresses DEFECT 988384 where we were sending the wrong datasource id (Unified DS id instead of Published DS id). See defect for more info
      contentId: content.contentType === DatasourceContentType.name ? (content as IDataSourceInfo).idForFavoriting : content.id,
      contentName: content.name,
      contentType: content.contentType,
      encodedShareLink: shareLink,
      isNamedSharingEnabled,
      toastHandler: toastHandler,
      shouldShareThumbnail: true, // When sharing from VizPortal, we always want to share the thumbnail for a view, not a VizSnapshot
      logEvent: telemetryLogger
    };

    const dialogOptions: ReactModalOptions = {
      ...getBaseDialogOptions(),
      title,
      dialogContent: React.createElement(ShareDialog, shareDialogProps)
    };

    modalInstance = yield call(ReactModalService.open, dialogOptions);

    // Log for what entity did we open the share dialog
    yield call(telemetryLogger, ShareDialogUserActionOptions.OpenShareDialog, content.contentType);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function getViewDetailInfo(content: IExplorableItemTypeInfo): IPromise<IViewDetailInfo> {
  const resource = ServiceLocator.get<Views>('Views');
  return resource.getById(content.id);
}

export { closeDialogHandler, getBaseDialogOptions, handleShareContentAction, telemetryLogger, toastHandler };
